import { httpClient } from '@bs/core'
import type { CustomerEcomOrderIdType } from '../../../features/Customer/adapters/types'
import { mapInsightsExtras } from './insightsExtras'
import { spendBucketToClvRange } from './pantryCustomerApis'
import type {
  Customer,
  CustomerCategorySummary,
  CustomerExportParams,
  CustomerExportResponse,
  CustomerInsights,
  CustomerListParams,
  CustomerListResponse,
  CustomerNoteInput,
  CustomerOrderListParams,
  CustomerOrderListResponse,
  CustomerProductSummary,
  CustomerRewardListResponse,
  CustomerShippingMethod,
  CustomerTimelineItem,
  CustomerUpdatePayload,
} from '../../../types/customer'

// ── Cova Customer gateway ─────────────────────────────────────────────────────
//
// Mirrors the legacy main-app hooks at:
//   src/pages/Customer/hooks/useCovaCustomerDetail.js
//   src/pages/Customer/hooks/useCovaCustomerNote.js
//   src/pages/Customer/hooks/useCustomerList.js   (cova list — yes, the legacy
//                                                  file is named generically)
//   src/pages/Customer/hooks/mapper.js
//
// Endpoint quirks specific to cova:
//   - Timeline path is `/timeline` (singular). Greenline uses `/timelines`.
//   - Delete note passes the id as a query param: `?note_id=ID`. Greenline uses
//     a path param.
//   - POS orders hit `POST /cova/orders/` with an action body. Greenline uses
//     `GET /greenline/stores/{storeId}/orders`.
//   - Insights call does NOT include `time_zone`. Greenline forces
//     `time_zone=America/Vancouver`.
//
// Response envelope: `httpClient.json.*` already unwraps `res.data.data`, so
// the resolved value is the inner payload directly. Do NOT re-unwrap.
//
// Many response shapes below are reconstructed from legacy destructuring sites
// rather than confirmed against a backend contract document. Those fields are
// flagged with `// VERIFY:` so a follow-up pass can validate them against real
// API responses before the UI lands.

// ── URL builders ──────────────────────────────────────────────────────────────

const customerListUrl = (storeId: string) => `/cova/stores/${storeId}/customers`
const customerDetailUrl = (storeId: string, customerId: string) =>
  `/cova/stores/${storeId}/customers/${customerId}`
const insightsUrl = (storeId: string, customerId: string) =>
  `/cova/stores/${storeId}/customers/${customerId}/insights`
const timelineUrl = (storeId: string, customerId: string) =>
  `/cova/stores/${storeId}/customers/${customerId}/timeline`
const exportUrl = (storeId: string) =>
  `/cova/stores/${storeId}/customers/export`
const productSearchUrl = (storeId: string) =>
  `/cova/stores/${storeId}/products/search`

const ECOM_ORDERS_URL = '/stores/woocommerce/orders/'
const POS_ORDERS_URL = '/cova/orders/'
const SHIPPING_METHODS_URL = '/stores/woocommerce/shipping/methods/'
const CATEGORIES_URL = '/stores/woocommerce/products/categories/'
const REWARDS_URL = '/stores/woocommerce/points_rewards'

// ── Wire shapes (confirmed against real API responses, 2026-05) ────────────────
//
// Real cova list response uses PascalCase top-level fields with nested
// `Shipping` / `Billing` objects (snake_case) for address + name parts.
// Loyalty fields land at the top level in snake_case. Example payload is
// captured in the agent-memory note for this module.

interface ApiCovaAddressBlock {
  first_name?: string
  last_name?: string
  company?: string
  address_1?: string | null
  address_2?: string | null
  city?: string | null
  state?: string | null
  postcode?: string | null
  country?: string | null
  phone?: string
  email?: string
}

interface ApiCustomerListItem {
  _id?: string
  Id?: string
  woo_customer_id?: number | string
  CustomerName?: string
  UniqueIdentifier?: string
  Email?: string
  Phone?: string
  State?: string | null
  CustomerSince?: string
  LastOrderDate?: string
  // Most recent recorded storefront activity (any customer_event). ISO-8601
  // UTC with a trailing `Z`, or null/absent. Surfaced as the list-only
  // "Last Seen" column.
  last_activity_gmt?: string
  TotalOrders?: number
  CompletedOrders?: number
  TotalPOSOrders?: number
  CLV?: number
  AverageOrderAmount?: number | null
  AverageOrderItems?: number | null
  // List endpoint surfaces these as PascalCase. Detail endpoint surfaces
  // the same data as lowercase `shipping` / `billing` (verified against
  // legacy `covaCustomerDetailMapper` in `src/pages/Customer/hooks/mapper.js`).
  // The mapper below reads both — keeping `shipping`/`billing` lowercase
  // optional here too lets the type system reflect the actual wire shape.
  Shipping?: ApiCovaAddressBlock
  Billing?: ApiCovaAddressBlock
  shipping?: ApiCovaAddressBlock
  billing?: ApiCovaAddressBlock
  points_balance?: number | null
  lifetime_earned_points?: number | null
  lifetime_redeem_points?: number | null
  loyalty_tier?: string | null
  // Legacy / detail-endpoint passthrough — kept so the detail mapper can lean
  // on the same interface if it ships flat snake_case fields.
  id?: string | number
  woo_id?: string | number
  first_name?: string
  last_name?: string
  full_name?: string
  email?: string
  phone?: string
  date_of_birth?: string
  // Legacy woo meta key for DOB carried by detail responses.
  _shipping_dob?: string
  age_verified?: boolean
  address_line_1?: string
  address_line_2?: string
  city?: string
  state?: string
  postcode?: string
  country?: string
  total_orders?: number
  clv?: number
  aov?: number
  points?: number
  last_order_date?: string
  customer_since_date?: string
  segment?: string
  [key: string]: unknown
}

interface ApiCustomerListResponse {
  items?: ApiCustomerListItem[]
  total_items?: number
  total_pages?: number
  page?: number
  limit?: number
  // Store-wide-but-filter-respecting aggregates computed by the backend over
  // the current search + filter set. `active_60d` = customers whose last order
  // was within 60 days; `new_30d` = customers who joined within 30 days.
  stats?: {
    total?: number
    active_60d?: number
    new_30d?: number
  }
}

interface ApiCustomerTimelineItem {
  // Cova returns timeline rows keyed by Mongo `_id`, not `id`. Keep both
  // declared so the mapper can fall back if the upstream shape ever shifts.
  _id?: string | number
  id?: string | number
  description?: string
  _created?: string
  _updated?: string
  operator_name?: string
  operator_photo?: string
  source?: string
  action?: string
}

interface ApiCustomerTimelineResponse {
  items?: ApiCustomerTimelineItem[]
  // VERIFY: list endpoint may inline the array at top-level rather than under
  // `items` — legacy `covaCustomerTimelineMapper` destructures both shapes.
  total_items?: number
}

interface ApiCustomerOrderShippingLine {
  method_id?: string
  method_title?: string
}

interface ApiCustomerOrderLineItem {
  parent_name?: string
  name?: string
  quantity?: number
}

interface ApiCustomerOrder {
  id?: string | number
  order_number?: string
  status?: string
  total?: number | string
  channel?: string
  // Legacy fields (kept for back-compat with older mocks/tests).
  fulfillment?: string
  date_created?: string
  items_preview?: string
  // Real woocommerce-shaped fields surfaced by `paginate_order`.
  item_count?: number
  line_items?: ApiCustomerOrderLineItem[]
  shipping_lines?: ApiCustomerOrderShippingLine[]
  fulfillment_status?: string
  fulfillment_type?: string
  fulfillment_list_type?: string
  location_name?: string
  date_created_gmt?: string
}

interface ApiCustomerOrderListResponse {
  items?: ApiCustomerOrder[]
  total_items?: number
  total_pages?: number
}

// Raw cova POS order (PascalCase, see real `/cova/orders/` paginate response).
interface ApiCovaPosOrder {
  Id?: string
  CreatedTime?: string
  ReceiptNumber?: string
  Status?: string
  OrderTotal?: number
  TotalValues?: number
  TotalItems?: number
  OrderFulfillment?: {
    Method?: string
    Status?: string
    Source?: string
  } | null
  InStock?: Array<{ Name?: string; Quantity?: number }>
}

interface ApiCovaPosOrderListResponse {
  items?: ApiCovaPosOrder[]
  total_items?: number
  total_pages?: number
}

interface ApiCustomerRewardLog {
  id?: string | number
  type?: 'earn' | 'redeem' | 'expire' | 'adjustment'
  points?: number
  balance_after?: number
  description?: string
  created_at?: string
}

interface ApiCustomerRewardLogResponse {
  items?: ApiCustomerRewardLog[]
  total_items?: number
  total_pages?: number
}


interface ApiCustomerFavoriteProduct {
  name?: string
  brand?: string | null
  // The backend ships either `{ id, name }` (variation category) or `{}` when
  // the variation has no category.
  category?: { id?: string | number; name?: string } | null
  // category_product is the "first categories" object on the parent product —
  // a fallback name source when the variation category is empty.
  category_product?: { id?: string | number; name?: string } | null
  catalog_id?: string
  variable_id?: number | string
  image?: string | null
  number_of_purchase?: number
  total_amount_spent?: number
  last_order_date?: string | null
}

interface ApiCustomerInsights {
  // KPI fields confirmed against the cova insights endpoint
  // (api/v2-inv/cova/stores/:storeId/customers/:posCustomerId/insights).
  // `latest_order` is the most recent order summary; only `CreatedTime` is
  // consumed by the hero stats strip today.
  clv?: number
  average_order_value?: number
  latest_order?: {
    CreatedTime?: string
    [key: string]: unknown
  }

  // Top-5 favourite products, pre-sorted by purchase count desc by the
  // backend. Drives the "Favourite Products" tab on the customer profile.
  top_5_preferences_product?: ApiCustomerFavoriteProduct[]

  retargeting_score?: number
  web_sessions_30d?: number
  top_searches?: Array<{ term: string; count: number }>
  abandoned_carts?: Array<{
    product_name: string
    price: number
    abandoned_at: string
  }>
  // VERIFY: insights endpoint payload not yet fully documented; treat unknown
  // fields as raw passthrough.
  [key: string]: unknown
}

const mapFavoriteProduct = (
  p: ApiCustomerFavoriteProduct,
): import('../../../types/customer').CustomerFavoriteProduct => ({
  name: p.name ?? '—',
  brand: p.brand ?? null,
  category: p.category?.name ?? p.category_product?.name ?? null,
  catalogId: p.catalog_id,
  variableId: p.variable_id,
  imageUrl: p.image ?? null,
  numberOfPurchase: p.number_of_purchase ?? 0,
  totalAmountSpent: p.total_amount_spent ?? 0,
  lastOrderDate: p.last_order_date ?? null,
})

// ── Param mapping ─────────────────────────────────────────────────────────────

// Sort-key translation. Right-hand tokens match the PascalCase field names on
// the real cova list response (`CustomerName`, `CLV`, `LastOrderDate`,
// `CustomerSince`, etc.). The `Points` token still mirrors the legacy main-app
// convention — `points_balance` is the response field but legacy sort uses
// `Points`; confirm with backend if the points column never reorders.
const sortByMap: Record<string, string> = {
  // Legacy keys (still used by older callers)
  total_orders: 'TotalOrders',
  fullname: 'CustomerName',
  average_order_amount: 'AverageOrderAmount',
  average_order_items: 'AverageOrderItems',
  clv: 'CLV',
  points: 'Points',
  last_order_date: 'LastOrderDate',
  // New Customer-list UI keys (CUSTOMER_SORT_KEYS). No `loyalty_points` entry
  // — `points` was dropped from `CUSTOMER_SORT_KEYS` because the backend
  // never populates a sortable points field.
  lifetime_value: 'CLV',
  last_order_at: 'LastOrderDate',
  joined_at: 'CustomerSince',
}

const buildListQuery = (
  params: CustomerListParams,
): Record<string, unknown> => {
  const query: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sortBy) {
    query.sort_by = sortByMap[params.sortBy] ?? params.sortBy
    // Backend uses Mongo-style sort direction — '1' asc / '-1' desc — not
    // the 'asc'/'desc' tokens the UI works with. See the legacy
    // CovaCustomerListV2 `Sorting` wiring for the same convention.
    if (params.sort) query.sort = params.sort === 'asc' ? '1' : '-1'
  }
  if (params.locationId) query.location_id = params.locationId
  if (params.segment && params.segment !== 'all') query.segment = params.segment
  if (params.activity && params.activity !== 'all')
    query.activity = params.activity
  if (params.channel && params.channel !== 'all') query.channel = params.channel
  if (params.spendBucket && params.spendBucket !== 'all') {
    Object.assign(query, spendBucketToClvRange(params.spendBucket))
  }
  // Store-specific extras (e.g. cova_product_ids, country, provinces) flow
  // through unchanged so the UI/legacy filter UX can pass them as-is during
  // the migration.
  if (params.extra) Object.assign(query, params.extra)
  return query
}

// ── Mappers (API → domain) ────────────────────────────────────────────────────

// Coerce nullish/empty strings to `undefined` so the UI can rely on
// `?? '—'` rendering instead of seeing literal "null" / "" in the cells.
const nz = (v: string | null | undefined): string | undefined => {
  if (v == null) return undefined
  const trimmed = v.trim()
  return trimmed === '' ? undefined : trimmed
}

const mapCustomer = (raw: ApiCustomerListItem, storeId: string): Customer => {
  // Canonical id priority: cova UUID `Id` → mongo `_id` → legacy `id`/`woo_id`
  // → `woo_customer_id` (numeric). Falls back to empty string so the row still
  // renders rather than throwing on the React key.
  const id = String(
    raw.Id ?? raw.id ?? raw._id ?? raw.woo_id ?? raw.woo_customer_id ?? '',
  )

  // List endpoint uses PascalCase `Shipping`/`Billing`; detail endpoint uses
  // lowercase `shipping`/`billing`. Coalesce both so the same mapper feeds
  // the list table and the edit-dialog initial values without the form
  // appearing empty on detail (the original bug).
  const ship = raw.Shipping ?? raw.shipping ?? {}
  const bill = raw.Billing ?? raw.billing ?? {}

  // First/last name aren't top-level on the cova list response — they live
  // inside `Shipping`/`Billing`. The display name (`CustomerName`) is already
  // concatenated, so we use it as-is and only fall back to the parts.
  const first =
    nz(ship.first_name) ?? nz(bill.first_name) ?? nz(raw.first_name) ?? ''
  const last =
    nz(ship.last_name) ?? nz(bill.last_name) ?? nz(raw.last_name) ?? ''
  const fullFromParts = [first, last].filter(Boolean).join(' ').trim()
  const full =
    nz(raw.CustomerName) ?? nz(raw.full_name) ?? fullFromParts ?? ''

  const address = {
    line1: nz(ship.address_1) ?? nz(bill.address_1) ?? nz(raw.address_line_1),
    line2: nz(ship.address_2) ?? nz(bill.address_2) ?? nz(raw.address_line_2),
    city: nz(ship.city) ?? nz(bill.city) ?? nz(raw.city),
    province:
      nz(ship.state) ?? nz(bill.state) ?? nz(raw.State) ?? nz(raw.state),
    postalCode: nz(ship.postcode) ?? nz(bill.postcode) ?? nz(raw.postcode),
    country: nz(ship.country) ?? nz(bill.country) ?? nz(raw.country),
  }

  // Preserve the original woocommerce-style shipping/billing blocks so the
  // edit dialog can round-trip them. Both blocks fall back to `undefined`
  // when the source response carries no nested object — consumers should
  // gate on truthiness before reading nested fields.
  const mapBlock = (
    block: ApiCovaAddressBlock | undefined,
  ): import('../../../types/customer').CustomerContactBlock | undefined =>
    block
      ? {
          firstName: nz(block.first_name),
          lastName: nz(block.last_name),
          company: nz(block.company),
          address1: nz(block.address_1),
          address2: nz(block.address_2),
          city: nz(block.city),
          state: nz(block.state),
          postcode: nz(block.postcode),
          country: nz(block.country),
          email: nz(block.email),
          phone: nz(block.phone),
        }
      : undefined

  // `_shipping_dob` is the canonical woo meta key carried by the detail
  // payload (legacy `data._shipping_dob`). Fall back to `date_of_birth` for
  // safety if the field is renamed server-side.
  const dob =
    nz(raw._shipping_dob as string | undefined) ??
    nz(raw.date_of_birth)

  return {
    id,
    storeId,
    storeType: 'cova',
    firstName: first,
    lastName: last,
    fullName: full,
    email: nz(raw.Email) ?? nz(bill.email) ?? nz(raw.email) ?? '',
    phone: nz(raw.Phone) ?? nz(ship.phone) ?? nz(bill.phone) ?? nz(raw.phone),
    dateOfBirth: dob,
    ageVerified: raw.age_verified,
    wooCustomerId: raw.woo_customer_id ?? raw.woo_id,
    address,
    shipping: mapBlock(raw.Shipping ?? raw.shipping),
    billing: mapBlock(raw.Billing ?? raw.billing),
    totalOrders: raw.TotalOrders ?? raw.total_orders,
    lifetimeValue: raw.CLV ?? raw.clv ?? undefined,
    avgOrderValue: raw.AverageOrderAmount ?? raw.aov ?? undefined,
    loyaltyPoints: raw.points_balance ?? raw.points ?? undefined,
    loyaltyTier: raw.loyalty_tier ?? undefined,
    lifetimeEarnedPoints: raw.lifetime_earned_points ?? undefined,
    lifetimeRedeemedPoints: raw.lifetime_redeem_points ?? undefined,
    lastOrderAt: raw.LastOrderDate ?? raw.last_order_date,
    lastActivityGmt: raw.last_activity_gmt,
    joinedAt: raw.CustomerSince ?? raw.customer_since_date,
    // segment / status / channels are still not present on the list
    // response. Re-enable when the backend lands them (or compute client-side
    // from order history) — see breadstack-customers.html mockup.
    segment: undefined,
    status: undefined,
    channels: undefined,
    raw,
  }
}

const mapTimelineItem = (
  raw: ApiCustomerTimelineItem,
): CustomerTimelineItem => ({
  id: String(raw._id ?? raw.id ?? ''),
  description: raw.description ?? '',
  createdAt: raw._created ?? raw._updated ?? '',
  operatorName: raw.operator_name,
  avatarUrl: raw.operator_photo,
  source: raw.source,
  action: raw.action,
})

// Convert `cantec_local_pickup` → `Cantec Local Pickup`. Used as a last
// fallback when the shipping line doesn't carry a `method_title`.
const titleFromId = (id: string): string =>
  id
    .split('_')
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')

const deriveItemsPreview = (raw: ApiCustomerOrder): string | undefined => {
  if (raw.items_preview) return raw.items_preview
  const items = raw.line_items ?? []
  if (items.length === 0) return undefined
  const first = items[0]
  const firstName = first?.parent_name || first?.name || ''
  if (!firstName) return undefined
  const extra = items.length - 1
  return extra > 0 ? `${firstName} + ${extra} more` : firstName
}

const deriveFulfillmentLabel = (raw: ApiCustomerOrder): string | undefined => {
  const first = raw.shipping_lines?.[0]
  if (first?.method_title?.trim()) return first.method_title.trim()
  if (first?.method_id) return titleFromId(first.method_id)
  if (raw.fulfillment_type) return titleFromId(raw.fulfillment_type)
  return raw.fulfillment
}

const mapOrder = (raw: ApiCustomerOrder) => ({
  id: String(raw.id ?? ''),
  orderNumber: raw.order_number ?? String(raw.id ?? ''),
  // VERIFY: cova legacy code derives channel from the source endpoint
  // (`/cova/orders/` → in_store, `/stores/woocommerce/orders/` → online).
  // For now we pass the raw channel through if present, else default by
  // caller (see listEcomOrders / listPosOrders).
  channel: (raw.channel as 'online' | 'in_store' | 'both') ?? 'online',
  // `fulfillment` keeps the raw identifier for back-compat; `fulfillmentLabel`
  // is the friendly string the table cell renders.
  fulfillment:
    raw.fulfillment ?? raw.fulfillment_list_type ?? raw.fulfillment_status,
  fulfillmentLabel: deriveFulfillmentLabel(raw),
  locationName: raw.location_name,
  status: raw.status ?? '',
  total: typeof raw.total === 'string' ? Number(raw.total) : (raw.total ?? 0),
  createdAt: raw.date_created_gmt ?? raw.date_created ?? '',
  itemCount: raw.item_count ?? raw.line_items?.length,
  itemsPreview: deriveItemsPreview(raw),
  raw,
})

const mapPosOrder = (raw: ApiCovaPosOrder) => {
  const items = raw.InStock ?? []
  const firstName = items[0]?.Name
  const extra = items.length - 1
  const itemsPreview = firstName
    ? extra > 0
      ? `${firstName} + ${extra} more`
      : firstName
    : undefined
  const fulfillmentMethod = raw.OrderFulfillment?.Method
  return {
    id: String(raw.Id ?? ''),
    orderNumber: raw.ReceiptNumber ?? String(raw.Id ?? ''),
    channel: 'in_store' as const,
    fulfillment: fulfillmentMethod,
    fulfillmentLabel: fulfillmentMethod
      ? titleFromId(fulfillmentMethod)
      : undefined,
    locationName: raw.OrderFulfillment?.Source,
    status: raw.Status ?? raw.OrderFulfillment?.Status ?? '',
    total: raw.OrderTotal ?? raw.TotalValues ?? 0,
    createdAt: raw.CreatedTime ?? '',
    itemCount: raw.TotalItems ?? (items.length || undefined),
    itemsPreview,
    raw,
  }
}

// ── Public gateway functions ──────────────────────────────────────────────────

export const covaListCustomers = async (
  storeId: string,
  params: CustomerListParams,
): Promise<CustomerListResponse> => {
  const raw = (await httpClient.json.get(customerListUrl(storeId), {
    params: buildListQuery(params),
  })) as ApiCustomerListResponse
  return {
    items: (raw?.items ?? []).map((item) => mapCustomer(item, storeId)),
    page: raw?.page ?? params.page,
    limit: raw?.limit ?? params.limit,
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
    stats: {
      total: raw?.stats?.total ?? raw?.total_items ?? 0,
      active: raw?.stats?.active_60d ?? 0,
      new: raw?.stats?.new_30d ?? 0,
    },
  }
}

export const covaGetCustomer = async (
  storeId: string,
  customerId: string,
): Promise<Customer> => {
  const raw = (await httpClient.json.get(
    customerDetailUrl(storeId, customerId),
  )) as ApiCustomerListItem
  return mapCustomer(raw, storeId)
}

// Translate a domain CustomerContactBlock into the woocommerce-style block
// (snake_case) that the cova PUT endpoint expects. Mirrors the legacy
// `revertDetailFormToApi` in `src/pages/Customer/hooks/mapper.js`.
//
// We coerce every field to a string (`?? ''`) so all 11 keys land on the
// wire even when the form value is empty. The backend treats the shipping
// block as a full replacement — sending only the populated keys would
// silently wipe the other fields. Matches legacy `revertDetailFormToApi`,
// which emitted all 11 keys verbatim from the form (and the form was
// pre-populated from the detail GET, never `undefined`).
const toApiBlock = (
  block?: import('../../../types/customer').CustomerContactBlock,
): Record<string, string> | undefined => {
  if (!block) return undefined
  return {
    first_name: block.firstName ?? '',
    last_name: block.lastName ?? '',
    company: block.company ?? '',
    address_1: block.address1 ?? '',
    address_2: block.address2 ?? '',
    city: block.city ?? '',
    postcode: block.postcode ?? '',
    country: block.country ?? '',
    state: block.state ?? '',
    email: block.email ?? '',
    phone: block.phone ?? '',
  }
}

export const covaUpdateCustomer = async (
  storeId: string,
  customerId: string,
  payload: CustomerUpdatePayload,
): Promise<Customer> => {
  // The legacy PUT body is a flat snake_case object with nested woocommerce
  // shipping/billing blocks plus the legacy `_shipping_dob` field. The mapping
  // below mirrors `revertDetailFormToApi` so the cova endpoint sees the
  // same shape it always has.
  const body: Record<string, unknown> = {}
  if (payload.firstName !== undefined) body.first_name = payload.firstName
  if (payload.lastName !== undefined) body.last_name = payload.lastName
  if (payload.email !== undefined) body.email = payload.email
  if (payload.phone !== undefined) body.phone = payload.phone
  if (payload.dateOfBirth !== undefined) body._shipping_dob = payload.dateOfBirth
  if (payload.gender !== undefined) body.gender = payload.gender

  const shippingBlock = toApiBlock(payload.shipping)
  if (shippingBlock) body.shipping = shippingBlock
  const billingBlock = toApiBlock(payload.billing)
  if (billingBlock) body.billing = billingBlock

  // Back-compat: callers that pass the flat `address` projection get a
  // shipping block synthesised from it, but only when neither shipping nor
  // billing was supplied explicitly.
  if (!shippingBlock && !billingBlock && payload.address) {
    body.shipping = {
      address_1: payload.address.line1,
      address_2: payload.address.line2,
      city: payload.address.city,
      state: payload.address.province,
      postcode: payload.address.postalCode,
      country: payload.address.country,
    }
  }

  if (payload.marketingEmail !== undefined)
    body.marketing_email = payload.marketingEmail
  if (payload.marketingSms !== undefined)
    body.marketing_sms = payload.marketingSms
  if (payload.extra) Object.assign(body, payload.extra)

  const raw = (await httpClient.json.put(
    customerDetailUrl(storeId, customerId),
    body,
  )) as ApiCustomerListItem
  return mapCustomer(raw, storeId)
}

// `resetPassword` moved to `pantryCustomerApis.ts::resetCustomerPassword` —
// the confirmed `/pantry/stores/:storeId/customers/:wooCustomerId/forgot-
// password` endpoint is identical across store types, so it no longer lives
// per-adapter here.

export const covaGetInsights = async (
  storeId: string,
  customerId: string,
  locationId?: string,
): Promise<CustomerInsights> => {
  const raw = (await httpClient.json.get(insightsUrl(storeId, customerId), {
    params: {
      // Legacy convention: a sentinel of `-1` means "no location filter".
      location_id: locationId && locationId !== '-1' ? locationId : null,
    },
  })) as ApiCustomerInsights
  return {
    clv: raw?.clv,
    averageOrderValue: raw?.average_order_value,
    latestOrderCreatedAt: raw?.latest_order?.CreatedTime,
    favoriteProducts: raw?.top_5_preferences_product?.map(mapFavoriteProduct),
    retargetingScore: raw?.retargeting_score,
    webSessions30d: raw?.web_sessions_30d,
    topSearches: raw?.top_searches,
    abandonedCarts: raw?.abandoned_carts?.map((c) => ({
      productName: c.product_name,
      price: c.price,
      abandonedAt: c.abandoned_at,
    })),
    // Additive fields for the extended Behavior UI, parsed from the same
    // payload (channel split, brand/category affinity, order deltas). See
    // `insightsExtras.ts`.
    ...mapInsightsExtras(raw),
    raw,
  }
}

export const covaListTimeline = async (
  storeId: string,
  customerId: string,
  limit = 20,
): Promise<CustomerTimelineItem[]> => {
  const raw = (await httpClient.json.get(timelineUrl(storeId, customerId), {
    params: { limit },
  })) as ApiCustomerTimelineResponse | ApiCustomerTimelineItem[]
  const items = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return items.map(mapTimelineItem)
}

export const covaAddNote = async (
  storeId: string,
  customerId: string,
  note: CustomerNoteInput,
): Promise<CustomerTimelineItem> => {
  const raw = (await httpClient.json.post(timelineUrl(storeId, customerId), {
    description: note.description,
  })) as ApiCustomerTimelineItem
  return mapTimelineItem(raw)
}

export const covaDeleteNote = async (
  storeId: string,
  customerId: string,
  noteId: string,
): Promise<void> => {
  // Cova quirk: note id is a query param, not a path segment.
  await httpClient.json.delete(timelineUrl(storeId, customerId), {
    params: { note_id: noteId },
  })
}

export const covaListEcomOrders = async (
  customerId: string,
  params: CustomerOrderListParams,
  idType: CustomerEcomOrderIdType = 'customer_id',
): Promise<CustomerOrderListResponse> => {
  const raw = (await httpClient.json.post(ECOM_ORDERS_URL, {
    action: 'paginate_order',
    parameters: {
      page: params.page,
      limit: params.limit,
      filters: { ...(params.filters ?? {}), [idType]: customerId },
      ...(params.sort ? { sort: params.sort } : {}),
    },
  })) as ApiCustomerOrderListResponse
  return {
    items: (raw?.items ?? []).map((item) => ({
      ...mapOrder(item),
      channel: 'online' as const,
    })),
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

export const covaListPosOrders = async (
  customerId: string,
  params: CustomerOrderListParams,
): Promise<CustomerOrderListResponse> => {
  const raw = (await httpClient.json.post(POS_ORDERS_URL, {
    action: 'paginate',
    parameters: {
      page: params.page,
      limit: params.limit,
      ...(params.sort ? { sort: params.sort } : {}),
      ...(params.filters ?? {}),
      customer_id: customerId,
    },
  })) as ApiCovaPosOrderListResponse
  return {
    items: (raw?.items ?? []).map(mapPosOrder),
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

export const covaListRewards = async (
  customerId: string,
  params: { page: number; limit: number },
): Promise<CustomerRewardListResponse> => {
  const raw = (await httpClient.json.post(REWARDS_URL, {
    action: 'read_log',
    parameters: {
      page: params.page,
      limit: params.limit,
      filters: { customer_id: customerId },
    },
  })) as ApiCustomerRewardLogResponse
  return {
    items: (raw?.items ?? []).map((r) => ({
      id: String(r.id ?? ''),
      type: r.type ?? 'earn',
      points: r.points ?? 0,
      balanceAfter: r.balance_after,
      description: r.description,
      createdAt: r.created_at ?? '',
    })),
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

export const covaExportCsv = async (
  storeId: string,
  params: CustomerExportParams,
): Promise<CustomerExportResponse> => {
  // Legacy export is `GET /cova/stores/{storeId}/customers/export?<filters>`.
  // The backend (`crm_be_cova_api/views/v2/customers.py:ExportCustomerView`)
  // returns one of two JSON shapes:
  //   • `{csv: "<csv text>"}` when the aggregation completes within
  //     MAX_TIME_EXPORT_AGGREGATE — the UI downloads the file inline.
  //   • `{export_id: "<id>"}` when the aggregation hits ExecutionTimeout —
  //     the backend queues an async job; the user is notified + emailed.
  //
  // The legacy main-app handler strips `page`/`limit`/`sort` from the params
  // before calling (we want the full filtered set, not the current page) and
  // forwards the user's UTC-offset as `time_zone`. We mirror both:
  //   - rebuild the query from `buildListQuery`, then delete pagination keys.
  //   - inject `time_zone` (snake_case — DRF serializer field name on the
  //     backend; the legacy frontend's camelCase `timeZone` was actually
  //     silently ignored, leaving CSV datetimes at UTC).
  const query = buildListQuery(params)
  delete query.page
  delete query.limit
  delete query.sort_by
  delete query.sort
  if (params.timeZone !== undefined) {
    query.time_zone = params.timeZone
  }
  return (await httpClient.json.get(exportUrl(storeId), {
    params: query,
  })) as unknown as CustomerExportResponse
}

export const covaListShippingMethods = async (
  storeId: string,
): Promise<CustomerShippingMethod[]> => {
  const raw = (await httpClient.json.post(SHIPPING_METHODS_URL, {
    store_id: storeId,
    action: 'read_direct_paginate',
    parameters: {},
  })) as
    | { items?: Array<{ id?: string | number; name?: string }> }
    | Array<{ id?: string | number; name?: string }>
  // VERIFY: shipping methods endpoint sometimes returns an array directly.
  const list = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return list.map((m) => ({ id: String(m.id ?? ''), name: m.name ?? '' }))
}

export const covaListCategories = async (): Promise<
  CustomerCategorySummary[]
> => {
  const raw = (await httpClient.json.post(CATEGORIES_URL, {
    action: 'list_all_only_name',
    parameters: {},
  })) as
    | Array<{ id?: string | number; name?: string }>
    | { items?: Array<{ id?: string | number; name?: string }> }
  const list = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return list.map((c) => ({ id: String(c.id ?? ''), name: c.name ?? '' }))
}

export const covaSearchProducts = async (
  storeId: string,
  query: string,
): Promise<CustomerProductSummary[]> => {
  const raw = (await httpClient.json.get(productSearchUrl(storeId), {
    params: { search: query },
  })) as
    | Array<{ id?: string | number; name?: string }>
    | { items?: Array<{ id?: string | number; name?: string }> }
  const list = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return list.map((p) => ({ id: String(p.id ?? ''), name: p.name ?? '' }))
}
